import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../../components/Seo';
import { useMarket } from '../../context/MarketContext';

const DocsHome: React.FC = () => {
  const { market } = useMarket();
  const isIndia = market === 'IN';

  return (
    <div className="pt-24">
      <Seo
        title="Docs | Cimmple"
        description={
          isIndia
            ? 'Official Cimmple documentation for India: CimmpleFlow shop operating system help and CimmpleRx pharmacy ERP help.'
            : 'Official Cimmple documentation: CimmplePay payroll help, CimmplePay API for integrations, and CimmpleFlow—the cloud operating system for machine shops (includes manufacturing ERP).'
        }
      />

      <section className="pt-8 pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3 text-balance">
            Cimmple Docs
          </h1>
          <p className="text-lg text-slate-700 dark:text-dark-300 max-w-3xl mx-auto text-balance">
            {isIndia
              ? 'Official guides for CimmpleFlow and CimmpleRx—product overviews and practical help for Indian teams.'
              : 'Official guides and technical documentation for Cimmple products—whether you run payroll, operate your shop with CimmpleFlow, or connect your own systems.'}
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {!isIndia && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 text-xs font-semibold uppercase tracking-wide">
                    CimmplePay
                  </span>
                  <p className="text-sm text-slate-600 dark:text-dark-400">
                    Choose between technical integration docs and operational help.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Link
                    to="/docs/cimmplepay"
                    className="block h-full rounded-2xl border-2 border-accent-300/70 dark:border-accent-500/40 bg-gradient-to-br from-accent-50 to-white dark:from-accent-500/10 dark:to-dark-900 p-6 hover:shadow-lg hover:shadow-accent-500/10 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 text-xs font-semibold mb-3">
                          API Docs
                        </span>
                        <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                          CimmplePay Integrations
                        </h2>
                        <p className="text-slate-700 dark:text-dark-300">
                          Authentication, endpoints, core flows, quick reference, and go-live checklist for developers.
                        </p>
                      </div>
                      <span className="text-accent-700 dark:text-accent-300 font-semibold whitespace-nowrap">Open</span>
                    </div>
                  </Link>

                  <Link
                    to="/help/cimmplepay"
                    className="block h-full rounded-2xl border-2 border-primary-300/70 dark:border-primary-500/40 bg-gradient-to-br from-primary-50 to-white dark:from-primary-500/10 dark:to-dark-900 p-6 hover:shadow-lg hover:shadow-primary-500/10 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 text-xs font-semibold mb-3">
                          Help Center
                        </span>
                        <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                          CimmplePay Operations Help
                        </h2>
                        <p className="text-slate-700 dark:text-dark-300">
                          Step-by-step payroll setup, pay runs, taxes, reports, and common support topics.
                        </p>
                      </div>
                      <span className="text-primary-700 dark:text-primary-300 font-semibold whitespace-nowrap">Open</span>
                    </div>
                  </Link>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 text-xs font-semibold uppercase tracking-wide">
                  CimmpleFlow
                </span>
                <p className="text-sm text-slate-600 dark:text-dark-400">
                  Product guidance and feature overviews.
                </p>
              </div>
              <Link
                to="/docs/cimmpleflow"
                className="block rounded-2xl border-2 border-slate-200 dark:border-dark-700 bg-white dark:bg-dark-900 p-6 hover:shadow-lg hover:shadow-slate-500/10 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 dark:bg-dark-800 text-slate-700 dark:text-dark-300 text-xs font-semibold mb-3">
                      Help Center
                    </span>
                    <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                      CimmpleFlow Docs
                    </h2>
                    <p className="text-slate-700 dark:text-dark-300">
                      Overview of CimmpleFlow as a shop operating system, including manufacturing ERP capabilities and practical guidance for teams.
                    </p>
                  </div>
                  <span className="text-slate-700 dark:text-dark-300 font-semibold whitespace-nowrap">Open</span>
                </div>
              </Link>
            </div>

            {isIndia && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-rx-100 dark:bg-rx-500/20 text-rx-700 dark:text-rx-300 text-xs font-semibold uppercase tracking-wide">
                    CimmpleRx
                  </span>
                  <p className="text-sm text-slate-600 dark:text-dark-400">
                    Pharmacy product guidance and feature overviews.
                  </p>
                </div>
                <Link
                  to="/docs/cimmplerx"
                  className="block rounded-2xl border-2 border-rx-300/70 dark:border-rx-500/40 bg-gradient-to-br from-rx-50 to-white dark:from-rx-500/10 dark:to-dark-900 p-6 hover:shadow-lg hover:shadow-rx-500/10 transition-all duration-300"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-rx-100 dark:bg-rx-500/20 text-rx-700 dark:text-rx-300 text-xs font-semibold mb-3">
                        Help Center
                      </span>
                      <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                        CimmpleRx Docs
                      </h2>
                      <p className="text-slate-700 dark:text-dark-300">
                        Overview of CimmpleRx for pharmacies—billing, batch inventory, GST, purchasing, and team access.
                      </p>
                    </div>
                    <span className="text-rx-700 dark:text-rx-300 font-semibold whitespace-nowrap">Open</span>
                  </div>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocsHome;
